import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';

/**
 * A search box for the top of a mat-select panel. Place it as the first child of the <mat-select>
 * and filter the <mat-option>s yourself from `searchChange`:
 *
 *   <mat-select formControlName="columnId" disableOptionCentering>
 *     <app-select-search [placeholder]="..." (searchChange)="term = $event"></app-select-search>
 *     <mat-option *ngFor="let c of filtered(term)" [value]="c.id">{{ c.name }}</mat-option>
 *   </mat-select>
 *
 * The select keeps owning the value, keyboard navigation and disabled state; this only adds the
 * input. Use `disableOptionCentering` on the select - Material positions the panel by option index,
 * which does not account for this header. The search clears whenever the panel closes.
 */
@Component({
  selector: 'app-select-search',
  templateUrl: './select-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectSearchComponent implements OnInit, OnDestroy {
  @Input() placeholder = '';
  /** Shown under the input when set - pass it only while the filtered list is empty. */
  @Input() noResultsText = '';
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;

  term = '';
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly select: MatSelect, private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.select.openedChange.pipe(takeUntil(this.destroy$)).subscribe(open => {
      if (open) {
        this.input.nativeElement.focus();
      } else if (this.term) {
        this.setTerm('');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInput(value: string): void {
    this.setTerm(value);
    //once the consumer has re-rendered the options, make the first match active so Enter picks it
    setTimeout(() => this.select._keyManager?.setFirstItemActive());
  }

  onKeydown(event: KeyboardEvent): void {
    //arrows, Enter, Escape and Tab stay with the select; anything typed belongs to the input -
    //otherwise Space selects the active option and letters jump through the select's type-ahead
    if (event.key.length === 1 || event.key === 'Home' || event.key === 'End') {
      event.stopPropagation();
    }
  }

  private setTerm(value: string): void {
    this.term = value;
    this.searchChange.emit(value);
    this.changeDetectorRef.markForCheck();
  }
}
